import React, { useCallback, useState, useEffect, useContext } from "react";
import Spinner from "@atlaskit/spinner";
import { Info, ListView, SpinnerContainer } from "./styled";
import { useDrop } from "react-dnd";
import Actions from "./Actions";
import CardContent from "./CardContent";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { ChartContext } from "../../context";
import { getUserValueFromField } from "../../../../helpers/cosmos-field-value-extractor";
import { ViewOptions } from "../ViewSelector";
import { useOrgChartStore } from "../../../../stores/use-org-chart-store";
import { DisplayFieldKeys } from "./CardWithDrop";

function ListWithDrop({
  node,
  userInfo,
  selectedUser,
  onUserClick,
  isLoading,
  hasChildren,
}) {
  const [firstElementValue, setFirstElementValue] = useState("");
  const [secondElementValue, setSecondElementValue] = useState("");
  const theme = useTheme();

  const {
    viewOptions,
    userRecords,
    fields,
    handleAddUsers,
    handleDeleteUser,
    isCosmosInstalled,
    isMacrosuiteInstalled,
  } = useContext(ChartContext);

  const { borderRadius, elementsToDisplay = {} } = viewOptions || {};
  const { t } = useTranslation();

  const { data } = useOrgChartStore((state) => ({
    data: state.data,
  }));

  const getElementToDisplayValue = useCallback(
    (fieldKey) => {
      if (!isCosmosInstalled) return "";

      const fieldId = data?.config?.[fieldKey];
      if (!fieldId || !node?.id) return "";

      const foundUserRecord = userRecords.find(
        (user) => user.accountId === node.id
      );
      if (!foundUserRecord) return "";

      const fieldData = fields.find((field) => field.id === fieldId);
      if (!fieldData) return "";

      return getUserValueFromField(foundUserRecord?.values, fields, fieldId);
    },
    [node?.id, fields, userRecords, data, isCosmosInstalled]
  );

  useEffect(() => {
    if (node?.id) {
      setFirstElementValue(
        getElementToDisplayValue(DisplayFieldKeys.TITLE_FIELD)
      );
      setSecondElementValue(
        getElementToDisplayValue(DisplayFieldKeys.FOOTER_FIELD)
      );
    }

    return () => {
      setFirstElementValue("");
      setSecondElementValue("");
    };
  }, [node?.id, getElementToDisplayValue]);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ["user"],
    drop: () => ({ type: "user", accountId: node?.id || "" }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  if (userInfo?.error) {
    return (
      <ListView>
        <Info>{t("failed-to-load-user")}</Info>
      </ListView>
    );
  }

  return (
    <ListView
      theme={theme}
      ref={drop}
      isHoveringOver={isOver}
      borderRadius={data?.config?.cardBorderRadius || 3}
      isSelected={selectedUser?.id === userInfo?.key}
      onClick={() => {
        if (userInfo?.key) onUserClick(userInfo?.key, userInfo?.name);
      }}
    >
      {isLoading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : (
        <CardContent
          viewType={ViewOptions.LIST}
          userInfo={userInfo}
          elements={{ firstElementValue, secondElementValue }}
        />
      )}
      <Actions
        node={node}
        userInfo={userInfo}
        hasChildren={hasChildren}
        handleAddUsers={handleAddUsers}
        handleDeleteUser={handleDeleteUser}
      />
    </ListView>
  );
}

export default ListWithDrop;
